import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { merge, Subscription } from 'rxjs';
import { AddFromGalleryPopupComponent } from '../add-from-gallery-popup/add-from-gallery-popup.component';
import { AnalyticsService } from '../core/services/analytics.service';
import { EventService } from '../core/services/event.service';
import { isUploadLive, reloadApp } from '../core/utils/reload-app';
import { UploadBannerService, UploadBannerState } from '../core/services/upload-banner.service';
import { SafeStorage } from '../core/utils/safe-storage';
import {
  BANNER_KEEP_OPEN,
  BANNER_STOPPED_ACTION,
  STOPPING_NOTE,
  STOPPING_TITLE,
  STOP_UPLOAD_CONFIRM,
  STOP_UPLOAD_KEEP,
  bannerDoneSubtitle,
  bannerDoneTitle,
  bannerStopped,
  stopUploadBody,
  stopUploadTitle
} from '../utils/upload-failure-copy';

/**
 * The persistent upload strip. Rendered by AppComponent as a sibling of
 * <router-outlet>, which is the whole point: event-detail already had a
 * minimized-upload indicator (styles and logic both still in
 * event-detail.component), but it was scoped to that one screen and never
 * wired to a template. This one outlives navigation.
 *
 * Gated by environment.features.persistentUploadBanner — off in prod.
 */
@Component({
  selector: 'app-upload-banner',
  templateUrl: './upload-banner.component.html',
  styleUrls: ['./upload-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UploadBannerComponent implements OnInit, OnDestroy {
  state: UploadBannerState | null = null;

  /**
   * ✕ on a running upload asks the same question as the dialog's ✕: keep
   * going, or stop. It used to offer only hiding, on a second tap — and the
   * question's text grew the strip upwards, moving the ✕ out from under the
   * finger, so the second tap usually missed.
   */
  confirmingStop = false;
  @ViewChild('keepBtn') keepBtn?: ElementRef<HTMLButtonElement>;

  readonly keepOpenText = BANNER_KEEP_OPEN;
  readonly stoppedAction = BANNER_STOPPED_ACTION;
  readonly keepLabel = STOP_UPLOAD_KEEP;
  readonly stopLabel = STOP_UPLOAD_CONFIRM;
  readonly stoppingTitle = STOPPING_TITLE;
  readonly stoppingNote = STOPPING_NOTE;
  readonly stopBody = (isHost?: boolean) => stopUploadBody(
    /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ? 'phone' : 'computer',
    !!isHost
  );
  stoppedText = bannerStopped;
  doneTitle = bannerDoneTitle;
  doneSubtitle = bannerDoneSubtitle;

  private sub?: Subscription;

  constructor(
    private uploadBanner: UploadBannerService,
    private dialog: MatDialog,
    private router: Router,
    private analytics: AnalyticsService,
    private eventService: EventService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>
  ) { }

  /**
   * Publishes how much of the bottom of the viewport this banner is covering,
   * as a `--upload-banner-offset` custom property on <html>.
   *
   * Anything else anchored to the bottom of the screen — the toast, today —
   * adds it to its own offset instead of disappearing underneath. Measured from
   * the rendered element rather than hardcoded, so it stays correct when the
   * banner is lifted above the install banner or padded by a safe-area inset.
   */
  private publishHeight(): void {
    const root = document.documentElement;
    const el = this.host.nativeElement.querySelector('.upload-banner') as HTMLElement | null;

    if (!el || this.dialogOpen) {
      root.style.removeProperty('--upload-banner-offset');
      return;
    }
    // Distance from the top of the banner to the bottom of the viewport, which
    // folds in the banner's own bottom offset and any safe-area padding.
    const covered = Math.max(0, Math.round(window.innerHeight - el.getBoundingClientRect().top));
    root.style.setProperty('--upload-banner-offset', `${covered}px`);
  }

  ngOnInit(): void {
    if (!this.uploadBanner.enabled) {
      return;
    }
    this.sub = this.uploadBanner.state$.subscribe((state) => {
      const appearing = !!state !== !!this.state;
      this.state = state;
      // The question only makes sense while photos are still being sent.
      const asking = this.confirmingStop;
      if (asking && (state?.phase !== 'uploading' || state.stopping)) {
        this.confirmingStop = false;
      }
      this.cdr.markForCheck();
      // Only on show/hide — the strip's height does not change as the numbers
      // tick. Deferred a frame so the element exists (or is gone) to measure.
      if (appearing || asking !== this.confirmingStop) {
        setTimeout(() => this.publishHeight());
      }
    });
    // The strip sits above every dialog (it has to outrank the page), which put
    // it over the photo viewer's toolbar and the Add Photos buttons. A dialog is
    // the person's whole attention, and the upload dialog shows its own progress,
    // so the strip steps aside until the last one closes.
    this.dialogSub = merge(this.dialog.afterOpened, this.dialog.afterAllClosed).subscribe(() => {
      const open = this.dialog.openDialogs.length > 0;
      if (open === this.dialogOpen) {
        return;
      }
      this.dialogOpen = open;
      this.cdr.markForCheck();
      setTimeout(() => this.publishHeight());
    });
    // A batch may already have been minimized before this component existed —
    // e.g. a reload while the snapshot is still fresh.
    this.uploadBanner.refresh();
  }

  /** True while any dialog is open. The strip is hidden then. */
  dialogOpen = false;
  private dialogSub?: Subscription;

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.dialogSub?.unsubscribe();
    // Leaving this set would push the toast up against nothing.
    document.documentElement.style.removeProperty('--upload-banner-offset');
  }

  /** A tap on the strip: to the upload's album, or its progress when already there. */
  openDialog(): void {
    const state = this.state;
    if (!state) {
      return;
    }

    // Finished: take them to the album the photos went into, and clear it.
    if (state.phase === 'done') {
      this.analytics.capture('upload_done_banner_tapped', {
        event_id: state.eventId,
        added: state.completed,
        failed: state.failed,
        navigated_away: !!state.returnUrl && this.router.url !== state.returnUrl,
      });
      this.uploadBanner.dismiss();
      this.goToAlbum(state);
      return;
    }

    // A stopped upload has nothing to reopen: the window that was running it is
    // gone. The useful thing to do is take them to the album to add the photos
    // again, and clear the notice so it does not follow them around.
    if (state.phase === 'stopped') {
      this.analytics.capture('upload_stopped_banner_tapped', {
        event_id: state.eventId,
        unfinished: state.remaining,
      });
      this.uploadBanner.dismiss();
      this.goToAlbum(state);
      return;
    }

    // Anywhere but this upload's own album, a tap means "take me there". It
    // used to go there only from another album page; from Home, Manage Event or
    // any other screen it opened the progress dialog on top of that screen.
    // The strip is still there on arrival (it comes back from storage), and a
    // tap on it there shows the progress. Needs the album's id: without one
    // there is nowhere to go, and showing progress beats a tap that does nothing.
    if (state.eventHex && !this.onAlbum(state.eventHex)) {
      this.analytics.capture('upload_banner_switch_album', {
        event_id: state.eventId,
        phase: state.phase,
        from: this.router.url.split('?')[0],
      });
      this.goToAlbum(state);
      return;
    }

    this.analytics.capture('upload_banner_reopened', {
      event_id: state.eventId,
      completed: state.completed,
      remaining: state.remaining,
      // Whether the banner actually earned its keep: was the user somewhere
      // other than the screen they started the upload from?
      navigated_away: !!state.returnUrl && this.router.url !== state.returnUrl,
    });

    // Handing ownership back to the dialog, which restores from the same
    // snapshot and keeps polling itself. Storage is left intact so the banner
    // can come back if the dialog is minimized again.
    this.uploadBanner.hide();

    this.dialog.open(AddFromGalleryPopupComponent, {
      panelClass: 'my-dialog-class',
      data: {
        eventId: state.eventId,
        userId: SafeStorage.getItem('u'),
        restoreProcessing: true,
        processingData: this.readSnapshot()
      },
      hasBackdrop: true,
      backdropClass: 'upload-dialog-backdrop',
      disableClose: true
    }).afterClosed().subscribe((result) => {
      if (result?.shouldRefresh && state.returnUrl && this.router.url !== state.returnUrl) {
        this.goToAlbum(state);
        return;
      }
      // Minimized again, or closed without finishing — put the strip back.
      this.uploadBanner.refresh();
    });
  }

  /** The album page, whichever album it is showing. Anchored: /open-event-detail is not it. */
  private get onAlbumPage(): boolean {
    return /^\/event-detail\//.test(this.router.url);
  }

  /** The album page, showing this album. */
  private onAlbum(hex: string): boolean {
    return this.onAlbumPage && SafeStorage.getItem('eventId') === hex;
  }

  /**
   * Back to the album the upload went into.
   *
   * The event page reads its album from localStorage('eventId') and, when that
   * is missing, treats the visitor as signed out: it clears storage, signs out
   * and sends them to login. Anywhere else in the app that key is dropped on
   * the way out of an album, so a banner tapped from another screen has to put
   * it back before navigating — otherwise a tap on "your photos are in" logs
   * the person out.
   */
  private goToAlbum(state: UploadBannerState): void {
    // `state.eventId` is the numeric id; the event page is keyed on the hex one
    // in localStorage('eventId'). Writing the numeric id there is what made the
    // page unable to open the album.
    const onAlbumPage = this.onAlbumPage;
    // The album is keyed on the hex id. Never fall back to localStorage's hex
    // while on an album page: that is the album being viewed, not the one the
    // upload belongs to, and it made "go to the album" decide it was already
    // there and do nothing.
    const hex = state.eventHex || (onAlbumPage ? null : SafeStorage.getItem('eventId'));
    if (!hex) {
      this.analytics.capture('upload_banner_no_album_id', { event_id: state.eventId });
      return;
    }
    // Already looking at this album: the gallery refreshes itself.
    if (onAlbumPage && SafeStorage.getItem('eventId') === hex) {
      return;
    }

    // The album page answers from a cache it does not refetch for two minutes,
    // and that is about how long people take to come back after an upload: a
    // host who opened the album, uploaded, and tapped "Your photos are in" from
    // Manage Event 82 seconds later got the album as it was before the photos.
    this.eventService.forgetAlbumPhotos(hex);

    // The album page seeds its header from the last card opened. Left in place
    // after a switch it would paint the other event's cover and title.
    SafeStorage.removeItem('eventData');
    SafeStorage.removeItem('formData');
    SafeStorage.setItem('eventId', hex);
    SafeStorage.setItem('e', state.eventId);
    if (state.eventName && state.eventName !== 'your Event Gallery' && state.eventName !== 'your album') {
      SafeStorage.setItem('event_name', state.eventName);
    }

    if (onAlbumPage) {
      // Same route, different album: the router reuses the mounted page and
      // never re-runs its setup, so it would keep showing the old event.
      // A reload would also end the very upload this banner is showing, so while
      // one is running the page is rebuilt by routing out and back instead —
      // through an empty route, not Home. Home's setup deletes the album keys
      // written above (and the album page treats a missing key as signed out),
      // and Home refuses link guests, which left their tap waiting on a reload
      // that is held until the upload ends.
      if (isUploadLive()) {
        this.router
          .navigateByUrl('/album-switch', { skipLocationChange: true })
          .then((ok) => {
            if (ok) {
              return this.router.navigateByUrl('/event-detail/true');
            }
            reloadApp('album_switch');
            return false;
          })
          .catch(() => reloadApp('album_switch'));
        return;
      }
      reloadApp('album_switch', { force: true });
      return;
    }
    this.router.navigateByUrl('/event-detail/true');
  }

  /** Photos of this batch neither sent nor given up on yet. */
  stopTitle(s: UploadBannerState): string {
    return stopUploadTitle(Math.max(0, s.total - s.uploaded - s.failed));
  }

  dismiss(event: Event): void {
    event.stopPropagation();
    const state = this.state;

    // A running upload this window can stop: ask, as the dialog's ✕ does.
    // Anything else — finished, stopped, already stopping, or an upload running
    // in another tab that nothing here can stop — just goes.
    if (state?.phase === 'uploading' && !state.stopping && this.uploadBanner.canStop) {
      this.confirmingStop = true;
      this.analytics.capture('upload_close_attempted', {
        event_id: state.eventId,
        phase: 'uploading',
        remaining: Math.max(0, state.total - state.uploaded - state.failed),
        from: 'banner',
      });
      this.cdr.markForCheck();
      setTimeout(() => {
        this.publishHeight();
        this.keepBtn?.nativeElement.focus();
      });
      return;
    }

    this.analytics.capture('upload_banner_dismissed', {
      event_id: state?.eventId,
      remaining: state?.remaining,
      phase: state?.phase,
    });
    this.uploadBanner.dismiss();
  }

  /** The strip stays: it is the only thing on screen showing the upload. */
  keepUploading(): void {
    this.confirmingStop = false;
    this.cdr.markForCheck();
    setTimeout(() => this.publishHeight());
  }

  /**
   * Same stop as the dialog's: nothing new is sent, the photos already on their
   * way finish, and the strip says so until the batch ends.
   */
  stopUpload(): void {
    this.confirmingStop = false;
    this.uploadBanner.requestStop();
    this.cdr.markForCheck();
    setTimeout(() => this.publishHeight());
  }

  private readSnapshot(): any {
    try {
      return JSON.parse(SafeStorage.getItem('uploadProcessingState') || '{}');
    } catch {
      return {};
    }
  }
}
